import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import {
    Row,
    Col,
    Card,
    CardBody,
    CardHeader,
    Button,
    FormGroup,
    Label,
    Input,
    Alert,
    Table,
    Badge,
    Spinner,
    Container,
    Modal,
    ModalHeader,
    ModalBody
} from 'reactstrap';
import { useTranslation } from 'react-i18next';
import Swal from 'sweetalert2';
import { FaMoneyBillWave, FaFileInvoice, FaTruck, FaPlus, FaMinus, FaList, FaUser, FaCalendarAlt, FaEdit, FaTrash, FaShieldAlt, FaArrowUp, FaArrowDown, FaBalanceScale, FaArrowLeft, FaSearch } from 'react-icons/fa';
import { reglementActions } from '../../sagas/reglementSlice';
import { formatPrice, preventScroll, roundToNearestInteger } from '../../helpers/utility';
import Select from 'react-select';
import DocumentSelectionModal from './DocumentSelectionModal';
import './ReglementForm.css'; // Assuming you have some custom styles
import { useLocation, useNavigate } from 'react-router-dom';
import { supplierActions } from '../../sagas/supplierSlice';
import { reglementsFournisseurActions } from '../../sagas/reglementFournisseurSlice';
import { invoiceSupplierActions } from '../../sagas/invoiceSupplierSlice';
import { receiptNoteActions } from '../../sagas/receiptNoteSlice';
import { companyActions } from '../../sagas/companySlice';
import axiosInstance from '../../services/api_helper';
import { GuidedPaymentStepper, PaymentFinancialSummary, PaymentHelp } from '../Common/GuidedPaymentUX';
import '../Common/GuidedPaymentUX.css';

const ReglementFournisseurForm = () => {
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const { invoices: facturesImpayees, loading: loadingInvoices } = useSelector((state) => state.invoiceSupplier);
    const { receiptNotes, isLoading } = useSelector((state) => state.receiptNote);
    const { suppliers } = useSelector(state => state.suppliers);
    const { company: defaultCompany, role } = useSelector((state) => state.auth);
    const { companies } = useSelector((state) => state.companies);
    const navigate = useNavigate();
    const location = useLocation();
    const routeReglement = location.state?.reglement || null;
    const [detailedReglement, setDetailedReglement] = useState(routeReglement);
    const reglement = detailedReglement || routeReglement;
    const quickPayment = location.state?.quickPayment || null;
    const quickDocuments = useMemo(
        () => (Array.isArray(quickPayment?.documents) ? quickPayment.documents : []),
        [quickPayment?.documents]
    );
    const [selectedFournisseur, setSelectedFournisseur] = useState(null);
    const [typeReglement, setTypeReglement] = useState(quickPayment?.typeReglement || 'AVANCE');
    const [paymentPurpose, setPaymentPurpose] = useState(
        quickPayment?.typeReglement === 'AVANCE' ? 'ADVANCE' : 'DOCUMENTS'
    );
    const [isCautionPayment, setIsCautionPayment] = useState((reglement?.typeReglement || quickPayment?.typeReglement) === 'CAUTION');
    const [selectedDocuments, setSelectedDocuments] = useState([]);
    const [showDocumentModal, setShowDocumentModal] = useState(false);
    const [availableAdvances, setAvailableAdvances] = useState([]);
    const [selectedAdvanceIds, setSelectedAdvanceIds] = useState([]);
    const advancePreferenceInitializedRef = useRef(false);
    const [loadingAdvances, setLoadingAdvances] = useState(false);
    const [avanceDocFilter, setAvanceDocFilter] = useState("NONE");
    const [selectedCompanyId, setSelectedCompanyId] = useState(null);
    const [documentFilters, setDocumentFilters] = useState({ query: '', amount: '' });
    const [documentSort, setDocumentSort] = useState({ key: 'date', direction: 'desc' });
    const [documentsInitialized, setDocumentsInitialized] = useState(false);
    const requestedInitialStep = Number(location.state?.initialStep || 1);
    const [activeStep, setActiveStep] = useState(Math.min(4, Math.max(1, requestedInitialStep)));
    const formikRef = useRef(null);
    const avanceDocFilterOptions = [
        { value: "NONE", label: t('reglement.none') },
        { value: "FACTURE", label: t('reglement.factures') },
        { value: "BR", label: t('reglement.boReceptions') },
        { value: "BOTH", label: t('reglement.both') }
    ];
    const typeReglementOptions = [
        { value: 'FACTURE', label: t('reglement.factures'), icon: FaFileInvoice },
        { value: 'BON_RECEPTION', label: t('reglement.boReceptions'), icon: FaTruck },
        { value: 'AVANCE', label: t('reglement.avance'), icon: FaMoneyBillWave },
        { value: 'CAUTION', label: t('reglement.caution'), icon: FaMoneyBillWave }
    ];
    const { loading: loadingReglements, error: errorReglements, echeances, cautions } = useSelector((state) => state.reglementsFournisseur);
    const [showEcheanceModal, setShowEcheanceModal] = useState(false);
    const [showCautionModal, setShowCautionModal] = useState(false);
    const [filters, setFilters] = useState({
        fournisseurId: null,
        dateFrom: null,
        dateTo: null,
        statut: "EN_ATTENTE"
    });
    const isSuperAdmin = role === 'ROLE_SUPER_ADMIN';
    const hasAffectation = Boolean(reglement && (
        (Array.isArray(reglement.allocations) && reglement.allocations.length > 0) ||
        Number(reglement.totalDocumentCoverage || 0) > 0 ||
        Number(reglement.montantAffecte || 0) > 0 ||
        Number(reglement.externalAdvanceApplied || reglement.montantAvancesUtilisees || 0) > 0
    ));
    const canEdit = !reglement || (reglement.statut !== 'ANNULE' && !hasAffectation);
    const resolvedDefaultCompanyId =
        reglement?.companyId ||
        reglement?.company?.id ||
        quickPayment?.companyId ||
        defaultCompany?.id ||
        companies?.[0]?.id ||
        null;

    useEffect(() => {
        if (!routeReglement?.id) {
            return;
        }

        let active = true;
        axiosInstance.get(`/api/reglements/fournisseur/${routeReglement.id}`)
            .then(({ data }) => {
                if (active && data) {
                    setDetailedReglement(data);
                    setDocumentsInitialized(false);
                }
            })
            .catch(() => {
                if (active) {
                    setDetailedReglement(routeReglement);
                }
            });

        return () => {
            active = false;
        };
    }, [routeReglement]);

    // Validation schema
    const validationSchema = Yup.object().shape({
        companyId: Yup.number()
            .transform((value, originalValue) => (originalValue === '' ? null : value))
            .nullable()
            .required(t('company.companyRequired', 'Entreprise requise')),
        fournisseurId: Yup.number().required(t('reglement.fournisseurRequired')),
        // montantRegle: Yup.number()
        //     .moreThan(0, t('reglement.montantPositive'))
        //     .required(t('reglement.montantRequired')),
        dateReglement: Yup.date().required(t('reglement.dateReglementRequired')),
        modeReglement: Yup.string().required(t('reglement.modeReglementRequired')),
        referencePaiement: Yup.string().when('modeReglement', {
            is: mode => ['VIREMENT', 'CARTE_BANCAIRE'].includes(mode),
            then: schema => schema.trim().required('La référence du paiement est obligatoire pour ce mode.'),
        }),
        numeroCheque: Yup.string().when('modeReglement', {
            is: mode => ['CHEQUE', 'TRAITE'].includes(mode),
            then: schema => schema.trim().required('Le numéro du chèque ou de la traite est obligatoire.'),
        }),
    });


    // Initial values
    const initialValues = {
        id: reglement?.id || null,
        reference: reglement?.reference || '',
        companyId: resolvedDefaultCompanyId,
        fournisseurId: reglement?.fournisseurId || quickPayment?.fournisseurId || '',
        numFactures: reglement?.numFactures || (quickPayment?.typeReglement === 'FACTURE' ? quickDocuments.map(doc => doc.numero).join(',') : ''),
        numBonReceptions: reglement?.numBonReceptions || (quickPayment?.typeReglement === 'BON_RECEPTION' ? quickDocuments.map(doc => doc.numero).join(',') : ''),
        typeReglement: reglement?.typeReglement || quickPayment?.typeReglement || 'FACTURE',
        montantRegle: reglement?.montantRegle || 0,
        dateReglement: reglement?.dateReglement || new Date().toLocaleDateString('en-CA', { timeZone: 'Africa/Casablanca' }),
        dateDecaissement: reglement?.dateDecaissement || '',
        statut: reglement?.statut || 'EN_ATTENTE',
        statutPrecedent: reglement?.statutPrecedent || null,
        observations: reglement?.observations || '',
        modeReglement: reglement?.modeReglement || 'ESPECES',
        referencePaiement: reglement?.referencePaiement || '',
        numeroCheque: reglement?.numeroCheque || '',
        banque: reglement?.banque || '',
        dateEcheance: reglement?.dateEcheance || '',
        motifAvance: reglement?.motifAvance || '',
        dateValiditeAvance: reglement?.dateValiditeAvance || '',
        commentaireAvance: reglement?.commentaireAvance || '',
    };

    const modeReglementOptions = [
        { value: 'ESPECES', label: t('reglement.especes') },
        { value: 'CHEQUE', label: t('reglement.cheque') },
        { value: 'TRAITE', label: t('reglement.traite') },
        { value: 'VIREMENT', label: t('reglement.virement') },
        { value: 'CARTE_BANCAIRE', label: t('reglement.carteBancaire') },
    ];

    const fournisseurOptions = suppliers?.map(sup => ({
        solde: sup.solde,
        totalDebit: sup.totalDebit,
        totalCredit: sup.totalCredit,
        value: sup.id,
        label: sup.typeFournisseur === 'PARTICULIER' ? `${sup.nom} ${sup.prenom} (${sup.code})` : `${sup.entreprise}(${sup.code})`,
    })) ?? [];

    const getDocumentTotalAmount = (document) =>
        Number(document?.montantTotalTTC ?? document?.montantTTC ?? 0);

    const getDocumentPaidAmount = (document) =>
        Number(document?.montantRegle ?? document?.montantPaye ?? 0);

    const getDocumentReturnAmount = (document) => Number(document?.montantRetourne ?? document?.montantAvoir ?? 0);
    const getDocumentNetAmount = (document) => Number(document?.montantNet ?? getDocumentTotalAmount(document));

    const isAllocationOwnedByCurrentOperation = (allocation) => {
        if (!reglement?.id) return true;
        const operationId = allocation?.reglementConsommateurId ?? allocation?.reglementId;
        return Number(operationId) === Number(reglement.id);
    };

    const getCurrentAllocation = (document) => Number(
        (Array.isArray(reglement?.allocations) ? reglement.allocations : [])
            .filter(allocation =>
                isAllocationOwnedByCurrentOperation(allocation) &&
                Number(allocation.documentId) === Number(document?.id) &&
                allocation.typeDocument === (document?.paymentDocumentType === 'FACTURE' ? 'FACTURE_ACHAT' : document?.paymentDocumentType)
            )
            .reduce((total, allocation) => total + Number(allocation?.montantAlloue || 0), 0)
    );

    const getDocumentRemainingAmount = (document) => {
        const backendRemaining = document?.montantRestant;
        const calculatedRemaining = backendRemaining !== undefined && backendRemaining !== null
            ? Number(backendRemaining)
            : Number(document?.montantNet ?? getDocumentTotalAmount(document)) - getDocumentPaidAmount(document);
        return Math.max(0, roundToNearestInteger(calculatedRemaining + getCurrentAllocation(document)));
    };

    const normalizeNumero = (value) => value?.toString().trim().toUpperCase() || '';

    const compactSelectStyles = {
        control: (base) => ({
            ...base,
            minHeight: '34px',
            fontSize: '0.875rem',
        }),
        valueContainer: (base) => ({
            ...base,
            padding: '0 8px',
        }),
        input: (base) => ({
            ...base,
            margin: 0,
            padding: 0,
        }),
        indicatorsContainer: (base) => ({
            ...base,
            height: '34px',
        }),
        clearIndicator: (base) => ({
            ...base,
            padding: '4px',
        }),
        dropdownIndicator: (base) => ({
            ...base,
            padding: '4px',
        }),
        menuPortal: base => ({ ...base, zIndex: 9999 }),
    };

    const isEditing = Boolean(reglement);
    const isQuickPayment = Boolean(quickPayment && !reglement);
    const isDocumentBoundType = selectedDocuments.length > 0 || typeReglement === 'FACTURE' || typeReglement === 'BON_RECEPTION';
    const canEditDocuments = canEdit;
    const canEditAmount = canEdit;

    const getTotalMontant = (items) => {
        return items.reduce((total, item) => total + item.montantRegle, 0);
    };

    const calculateDocumentsAmount = (documents = []) =>
        roundToNearestInteger(documents.reduce((total, doc) => total + Number(doc.montantAAffecter || 0), 0));

    const externalAdvances = useMemo(
        () => availableAdvances.filter(avance => Number(avance.reglementId) !== Number(reglement?.id)),
        [availableAdvances, reglement?.id]
    );

    const totalAvailableAdvances = useMemo(
        () => externalAdvances.reduce((total, avance) => total + Number(avance.montantDisponible || 0), 0),
        [externalAdvances]
    );

    const buildSelectedAdvanceUsages = (documents = selectedDocuments) => {
        let remaining = calculateDocumentsAmount(documents);
        if (remaining <= 0 || !externalAdvances.length || !selectedAdvanceIds.length) {
            return [];
        }

        return externalAdvances.reduce((usages, avance) => {
            if (remaining <= 0) return usages;
            if (!selectedAdvanceIds.includes(avance.id)) return usages;
            const disponible = Number(avance.montantDisponible || 0);
            const montantUtilise = Math.min(disponible, remaining);
            if (montantUtilise > 0) {
                usages.push({
                    avanceId: avance.id,
                    montantUtilise,
                    commentaire: `Utilisation avance ${avance.referenceReglement || avance.reglementId || avance.id}`
                });
                remaining -= montantUtilise;
            }
            return usages;
        }, []);
    };

    const calculateAdvanceUsageAmount = (documents = selectedDocuments) =>
        buildSelectedAdvanceUsages(documents).reduce((total, usage) => total + Number(usage.montantUtilise || 0), 0);

    const calculateSelectedAdvanceCapacity = () => externalAdvances.reduce(
        (total, avance) => selectedAdvanceIds.includes(avance.id)
            ? total + Math.max(0, Number(avance.montantDisponible || 0))
            : total,
        0
    );

    const applyAvailableAdvancesFirst = (setFieldValue) => {
        let remaining = calculateDocumentsAmount(selectedDocuments);
        const advanceIds = [];

        externalAdvances.forEach(advance => {
            if (remaining <= 0) return;
            const available = Math.max(0, Number(advance.montantDisponible || 0));
            if (available <= 0) return;
            advanceIds.push(advance.id);
            remaining = Math.max(0, remaining - available);
        });

        setSelectedAdvanceIds(advanceIds);
        setFieldValue('montantRegle', roundToNearestInteger(remaining));
    };

    const calculateCurrentPaymentAllocation = (documents = selectedDocuments) =>
        Math.max(0, calculateDocumentsAmount(documents) - calculateAdvanceUsageAmount(documents));

    const allocateDocumentsAutomatically = (documents, paymentAmount) => {
        let remainingBudget = Math.max(0, Number(paymentAmount || 0));
        return documents.map(document => {
            const documentBalance = getDocumentRemainingAmount(document);
            const allocated = Math.min(documentBalance, remainingBudget);
            remainingBudget = Math.max(0, remainingBudget - allocated);
            return { ...document, montantAAffecter: roundToNearestInteger(allocated) };
        });
    };

    const syncMontantRegleFromDocuments = (documents) => {
        if (reglement) return;
        const amount = documents.reduce((total, document) => total + getDocumentRemainingAmount(document), 0);
        formikRef.current?.setFieldValue('montantRegle', roundToNearestInteger(amount));
    };

    const applyExistingPaymentBudget = (documents) => reglement
        ? allocateDocumentsAutomatically(documents, formikRef.current?.values?.montantRegle ?? reglement.montantRegle)
        : documents;

    const goToStep = (step) => {
        setActiveStep(step);
        document.getElementById(`supplier-payment-step-${step}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };

    useEffect(() => {
        dispatch(supplierActions.search({
            query: ""
        }));
        if (!companies || companies.length === 0) {
            dispatch(companyActions.getAll({}));
        }
    }, [companies, dispatch]);

    useEffect(() => {
        setSelectedCompanyId(resolvedDefaultCompanyId);
    }, [resolvedDefaultCompanyId]);

    useEffect(() => {
        if (!isQuickPayment) {
            return;
        }

        setTypeReglement(quickPayment.typeReglement || 'FACTURE');
        setPaymentPurpose(quickPayment.typeReglement === 'AVANCE' ? 'ADVANCE' : 'DOCUMENTS');
        setIsCautionPayment(quickPayment.typeReglement === 'CAUTION');
        setSelectedCompanyId(quickPayment.companyId || resolvedDefaultCompanyId);
        setSelectedDocuments([]);
        setDocumentsInitialized(false);
    }, [isQuickPayment, quickPayment, quickDocuments, resolvedDefaultCompanyId]);

    useEffect(() => {
        if (!isQuickPayment || !quickPayment?.fournisseurId || !suppliers?.length) {
            return;
        }

        const fournisseur = suppliers.find(item => Number(item.id) === Number(quickPayment.fournisseurId));
        if (!fournisseur) {
            return;
        }

        setSelectedFournisseur({
            value: fournisseur.id,
            label: fournisseur.typeFournisseur === 'PARTICULIER'
                ? `${fournisseur.prenom || ''} ${fournisseur.nom || ''}`.trim()
                : fournisseur.entreprise,
            solde: fournisseur.solde,
            totalDebit: fournisseur.totalDebit,
            totalCredit: fournisseur.totalCredit,
        });
        setFilters(f => ({ ...f, fournisseurId: fournisseur.id }));
        dispatch(reglementsFournisseurActions.echeanceFournisseur({ fournisseurId: fournisseur.id, statut: "EN_ATTENTE" }));
        dispatch(reglementsFournisseurActions.cautionFournisseur({ fournisseurId: fournisseur.id, statut: "EN_ATTENTE" }));
    }, [dispatch, isQuickPayment, quickPayment?.fournisseurId, suppliers]);

    // Initialize form with existing reglement data
    useEffect(() => {
        if (reglement) {
            // Set selected fournisseur
            const fournisseur = suppliers.find(c => c.id === reglement.fournisseurId);
            if (fournisseur) {
                setSelectedFournisseur({
                    value: fournisseur.id,
                    label: fournisseur.typeFournisseur === 'PARTICULIER'
                        ? `${fournisseur.prenom} ${fournisseur.nom}`
                        : fournisseur.entreprise,
                    solde: fournisseur.solde,
                    totalDebit: fournisseur.totalDebit,
                    totalCredit: fournisseur.totalCredit,
                });
                dispatch(reglementsFournisseurActions.echeanceFournisseur({ ...filters, fournisseurId: fournisseur.id }));
                dispatch(reglementsFournisseurActions.cautionFournisseur({ ...filters, fournisseurId: fournisseur.id }));
            }

            // Set type based on existing data
            setTypeReglement(reglement.typeReglement);
            setPaymentPurpose(reglement.typeReglement === 'AVANCE' ? 'ADVANCE' : 'DOCUMENTS');
            setIsCautionPayment(reglement.typeReglement === 'CAUTION');
            setSelectedCompanyId(reglement.companyId || reglement.company?.id || resolvedDefaultCompanyId);

        }
    }, [reglement, suppliers, resolvedDefaultCompanyId]);


    // Load documents when fournisseur/company/type changes
    useEffect(() => {
        if (selectedFournisseur && selectedCompanyId) {
            dispatch(invoiceSupplierActions.search({
                fournisseurId: selectedFournisseur.value,
                companyId: selectedCompanyId,
            }));
            dispatch(receiptNoteActions.search({
                fournisseurId: selectedFournisseur.value,
                companyId: selectedCompanyId,
                sansFacture: true,
            }));
        }

    }, [selectedFournisseur, dispatch, selectedCompanyId]);

    useEffect(() => {
        if (!selectedFournisseur?.value) {
            advancePreferenceInitializedRef.current = false;
            setAvailableAdvances([]);
            setSelectedAdvanceIds([]);
            return;
        }

        let cancelled = false;
        advancePreferenceInitializedRef.current = true;
        setLoadingAdvances(true);
        setAvailableAdvances([]);
        setSelectedAdvanceIds([]);
        axiosInstance.get(`/api/avances/fournisseur/${selectedFournisseur.value}/disponibles`, {
            params: selectedCompanyId
                ? { companyId: selectedCompanyId, currentReglementId: reglement?.id || undefined }
                : { currentReglementId: reglement?.id || undefined }
        })
            .then(({ data }) => {
                if (!cancelled) {
                    advancePreferenceInitializedRef.current = Boolean(reglement);
                    setAvailableAdvances(Array.isArray(data) ? data : []);
                    const existingUsages = Array.isArray(reglement?.utilisationAvances) ? reglement.utilisationAvances : [];
                    setSelectedAdvanceIds(existingUsages.map(usage => usage.avanceId));
                }
            })
            .catch(() => {
                if (!cancelled) {
                    setAvailableAdvances([]);
                }
            })
            .finally(() => {
                if (!cancelled) {
                    setLoadingAdvances(false);
                }
            });

        return () => {
            cancelled = true;
        };
    }, [selectedFournisseur?.value, selectedCompanyId, reglement?.id, reglement?.utilisationAvances]);

    useEffect(() => {
        if (
            reglement || loadingAdvances || advancePreferenceInitializedRef.current ||
            paymentPurpose !== 'DOCUMENTS' || selectedDocuments.length === 0 ||
            externalAdvances.length === 0
        ) return;

        advancePreferenceInitializedRef.current = true;
        applyAvailableAdvancesFirst(formikRef.current?.setFieldValue);
    }, [reglement, loadingAdvances, paymentPurpose, selectedDocuments, externalAdvances]);

    // Handle document selection from modal
    const handleDocumentsSelected = (documents) => {
        if (!canEditDocuments) {
            return;
        }
        if (documents.length > 0) {
            setIsCautionPayment(false);
            setTypeReglement(documents[0].paymentDocumentType || typeReglement);
        }
        const allocatedDocuments = documents.reduce((result, document) => {
            result.push({ ...document, montantAAffecter: getDocumentRemainingAmount(document) });
            return result;
        }, []);
        setSelectedDocuments(applyExistingPaymentBudget(allocatedDocuments));
        syncMontantRegleFromDocuments(documents);
    };

    const toggleDocumentSelection = (document, isSelected) => {
        if (!canEditDocuments) {
            return;
        }
        setSelectedDocuments(prev => {
            let nextDocuments;
            if (isSelected) {
                setIsCautionPayment(false);
                setTypeReglement(document.paymentDocumentType || typeReglement);
                if (prev.some(doc => doc.id === document.id)) {
                    return prev;
                }
                nextDocuments = [...prev, { ...document, montantAAffecter: getDocumentRemainingAmount(document) }];
            } else {
                nextDocuments = prev.filter(doc => doc.id !== document.id);
            }

            const allocatedDocuments = applyExistingPaymentBudget(nextDocuments);
            syncMontantRegleFromDocuments(allocatedDocuments);
            return allocatedDocuments;
        });
    };

    // Calculate total amount
    const calculateTotalAmount = () => calculateDocumentsAmount(selectedDocuments);


    // Remove document from selection
    const removeDocument = (documentId) => {
        if (!canEditDocuments) {
            return;
        }
        setSelectedDocuments(prev => {
            const nextDocuments = prev.filter(doc => doc.id !== documentId);
            const allocatedDocuments = applyExistingPaymentBudget(nextDocuments);
            syncMontantRegleFromDocuments(allocatedDocuments);
            return allocatedDocuments;
        });
    };

    // Handle form submission
    const getSelectedInvoices = () =>
        selectedDocuments.filter(doc => Number(doc.montantAAffecter || 0) > 0 && (doc.paymentDocumentType === 'FACTURE' || (typeReglement === 'FACTURE' && !doc.paymentDocumentType)));

    const getSelectedReceiptNotes = () =>
        selectedDocuments.filter(doc => Number(doc.montantAAffecter || 0) > 0 && (doc.paymentDocumentType === 'BON_RECEPTION' || (typeReglement === 'BON_RECEPTION' && !doc.paymentDocumentType)));

    const resolveEffectiveTypeReglement = () => {
        if (isCautionPayment && selectedDocuments.length === 0) return 'CAUTION';
        if (paymentPurpose === 'ADVANCE') return 'AVANCE';
        if (selectedDocuments.length === 0) return 'AVANCE';
        return getSelectedInvoices().length > 0 ? 'FACTURE' : 'BON_RECEPTION';
    };

    const getBusinessErrorMessage = (error) =>
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        t('common.errorOccurred');

    const handleSubmit = async (values, { setSubmitting }) => {
        if (paymentPurpose === 'DOCUMENTS' && selectedDocuments.length === 0) {
            const choice = await Swal.fire({
                icon: 'question',
                title: 'Aucun document sélectionné',
                text: 'Voulez-vous enregistrer la totalité du paiement comme avance fournisseur ?',
                showCancelButton: true,
                confirmButtonText: 'Enregistrer comme avance',
                cancelButtonText: 'Revenir à la sélection'
            });
            if (!choice.isConfirmed) {
                setActiveStep(2);
                setSubmitting(false);
                return;
            }
            setPaymentPurpose('ADVANCE');
            setTypeReglement('AVANCE');
        }
        if (
            paymentPurpose === 'DOCUMENTS' &&
            selectedDocuments.length > 0 &&
            totalAvailableAdvances > 0 &&
            selectedAdvanceIds.length === 0 &&
            Number(values.montantRegle || 0) > 0
        ) {
            const choice = await Swal.fire({
                icon: 'warning',
                title: 'Avances disponibles non utilisées',
                html: `<div class="text-start"><p>Ce fournisseur dispose de <b>${formatPrice(totalAvailableAdvances)}</b> d’avances pour cette entreprise.</p><p>Vous avez saisi un nouveau montant sans utiliser ces avances.</p></div>`,
                showCancelButton: true,
                showDenyButton: true,
                confirmButtonText: 'Continuer avec le nouveau montant',
                denyButtonText: 'Utiliser les avances',
                cancelButtonText: 'Revenir au formulaire'
            });
            if (choice.isDenied) {
                applyAvailableAdvancesFirst(formikRef.current?.setFieldValue);
                setActiveStep(3);
                setSubmitting(false);
                return;
            }
            if (!choice.isConfirmed) {
                setActiveStep(3);
                setSubmitting(false);
                return;
            }
        }
        const effectiveTypeReglement = paymentPurpose === 'ADVANCE' || selectedDocuments.length === 0
            ? 'AVANCE'
            : resolveEffectiveTypeReglement();
        const selectedInvoices = getSelectedInvoices();
        const selectedReceiptNotes = getSelectedReceiptNotes();
        const automaticAdvanceUsages =
            effectiveTypeReglement === 'AVANCE' || effectiveTypeReglement === 'CAUTION'
                ? []
                : buildSelectedAdvanceUsages(selectedDocuments);
        const totalAvancesUtilisees = automaticAdvanceUsages.reduce((total, usage) => total + Number(usage.montantUtilise || 0), 0);
        const totalAllocated = calculateTotalAmount();
        const currentPaymentAllocated = Math.max(0, totalAllocated - totalAvancesUtilisees);
        const invalidDocument = selectedDocuments.find(document =>
            Number(document.montantAAffecter || 0) > getDocumentRemainingAmount(document)
        );
        if (invalidDocument) {
            Swal.fire({ icon: 'error', title: 'Répartition incorrecte', text: `L’affectation dépasse le solde du document ${invalidDocument.numero}.` });
            setActiveStep(3);
            setSubmitting(false);
            return;
        }
        if (currentPaymentAllocated > Number(values.montantRegle || 0)) {
            Swal.fire({ icon: 'error', title: t('common.error'), text: 'La part affectée depuis ce règlement dépasse le montant payé.' });
            setActiveStep(3);
            setSubmitting(false);
            return;
        }
        const isEcheancePayment = ['CHEQUE', 'TRAITE'].includes(values.modeReglement);
        const payload = {
            data: {
                ...values,
                numeroCheque: isEcheancePayment ? values.numeroCheque : null,
                banque: isEcheancePayment ? values.banque : null,
                dateEcheance: isEcheancePayment ? values.dateEcheance : null,
                companyId: values.companyId ? Number(values.companyId) : null,
                typeReglement: effectiveTypeReglement,
                numFactures: selectedInvoices.map(doc => doc.numero).filter(Boolean).join(','),
                numBonReceptions: selectedReceiptNotes.map(doc => doc.numero).filter(Boolean).join(','),
                montantRegle: roundToNearestInteger(values.montantRegle),
                documentsIds: effectiveTypeReglement === 'AVANCE' || effectiveTypeReglement === 'CAUTION' ? [] : selectedDocuments.filter(doc => Number(doc.montantAAffecter || 0) > 0).map(doc => doc.id),
                estAvance: effectiveTypeReglement === 'AVANCE',
                utilisationAvances: automaticAdvanceUsages,
                montantAvancesUtilisees: totalAvancesUtilisees,
                montantReglementNet: Number(values.montantRegle || 0),
                allocations: selectedDocuments.filter(document => Number(document.montantAAffecter || 0) > 0).map(document => ({
                    documentId: document.id,
                    typeDocument: document.paymentDocumentType === 'FACTURE' ? 'FACTURE_ACHAT' : document.paymentDocumentType,
                    numeroDocument: document.numero,
                    montantDocument: getDocumentTotalAmount(document),
                    montantAlloue: roundToNearestInteger(document.montantAAffecter),
                    montantRestant: Math.max(0, roundToNearestInteger(getDocumentRemainingAmount(document) - Number(document.montantAAffecter || 0)))
                })),
                dateValiditeAvance: effectiveTypeReglement === 'AVANCE' ? values.dateValiditeAvance : null,
                commentaireAvance: effectiveTypeReglement === 'AVANCE' ? values.commentaireAvance : null,
            },
            onSuccess: (data) => {
                const unapplied = Math.max(0, Number(values.montantRegle || 0) - currentPaymentAllocated);
                Swal.fire({
                    icon: 'success',
                    title: reglement ? 'Paiement mis à jour' : 'Paiement fournisseur enregistré',
                    html: `<div class="text-start"><p>Référence : <b>${data?.reference || values.reference || data?.id || '-'}</b></p><ul><li>Affecté depuis ce règlement : <b>${formatPrice(currentPaymentAllocated)}</b></li><li>Avances externes utilisées : <b>${formatPrice(totalAvancesUtilisees)}</b></li><li>Total couvert : <b>${formatPrice(totalAllocated)}</b></li><li>Reste disponible : <b>${formatPrice(unapplied)}</b></li><li>Documents mis à jour : <b>${selectedDocuments.length}</b></li></ul></div>`,
                });
                navigate("/purchases-module/payments/fournisseur", {
                    state: { recentPaymentId: data?.id || reglement?.id || null }
                });
            },
            onError: (error) => {
                Swal.fire({
                    icon: 'error',
                    title: t('common.error'),
                    text: getBusinessErrorMessage(error),
                });
                setSubmitting(false);
            }
        };

        if (reglement) {
            const advanceAfter = Math.max(0, Number(values.montantRegle || 0) - currentPaymentAllocated);
            const confirmation = await Swal.fire({
                icon: 'info',
                title: 'Impact de la modification',
                html: `<div class="text-start"><p>Cette modification va recalculer les documents, soldes et mouvements concernés.</p><ul><li>Montant : <b>${formatPrice(reglement.montantRegle || 0)}</b> vers <b>${formatPrice(values.montantRegle || 0)}</b></li><li>Affecté depuis ce règlement : <b>${formatPrice(currentPaymentAllocated)}</b></li><li>Avances externes utilisées : <b>${formatPrice(totalAvancesUtilisees)}</b></li><li>Total couvert : <b>${formatPrice(totalAllocated)}</b></li><li>Documents : <b>${selectedDocuments.length}</b></li><li>Reste disponible après modification : <b>${formatPrice(advanceAfter)}</b></li></ul></div>`,
                showCancelButton: true,
                confirmButtonText: 'Confirmer la modification',
                cancelButtonText: 'Continuer la modification'
            });
            if (!confirmation.isConfirmed) {
                setSubmitting(false);
                return;
            }
            dispatch(reglementsFournisseurActions.updateRequest({ ...payload, id: reglement.id }));
        } else {
            const unapplied = Math.max(0, Number(values.montantRegle || 0) - currentPaymentAllocated);
            const confirmation = await Swal.fire({
                icon: 'question',
                title: effectiveTypeReglement === 'AVANCE' ? 'Confirmer l’avance fournisseur' : 'Confirmer le paiement fournisseur',
                html: `<div class="text-start"><p>Vérifiez les conséquences avant confirmation.</p><ul><li>Montant payé : <b>${formatPrice(values.montantRegle || 0)}</b></li><li>Affecté depuis ce règlement : <b>${formatPrice(currentPaymentAllocated)}</b></li><li>Avances externes utilisées : <b>${formatPrice(totalAvancesUtilisees)}</b></li><li>Total couvert : <b>${formatPrice(totalAllocated)}</b></li><li>Reste disponible sur ce règlement : <b>${formatPrice(unapplied)}</b></li><li>Documents concernés : <b>${selectedDocuments.length}</b></li></ul><p>Après confirmation, les soldes et statuts des documents sélectionnés seront recalculés.</p></div>`,
                showCancelButton: true,
                confirmButtonText: effectiveTypeReglement === 'AVANCE' ? 'Enregistrer l’avance' : 'Confirmer le paiement fournisseur',
                cancelButtonText: 'Revenir au formulaire',
            });
            if (!confirmation.isConfirmed) {
                setSubmitting(false);
                return;
            }
            dispatch(reglementsFournisseurActions.createRequest(payload));
        }
    };

    const availableDocuments = [
        ...facturesImpayees
            .filter(f => f.statut !== 'ANNULEE' && f.statut !== 'PAYEE')
            .map(doc => ({ ...doc, paymentDocumentType: 'FACTURE' })),
        ...receiptNotes
            .map(doc => ({ ...doc, paymentDocumentType: 'BON_RECEPTION' })),
    ];

    const filteredAvailableDocuments = (Array.isArray(availableDocuments) ? availableDocuments : []).filter((document) => {
        const search = documentFilters.query.trim().toUpperCase();
        const amount = Number(documentFilters.amount || 0);
        const remaining = getDocumentRemainingAmount(document);
        const matchesSearch =
            !search ||
            normalizeNumero(document.numero).includes(search) ||
            normalizeNumero(document.reference).includes(search) ||
            normalizeNumero(document.fournisseurNom).includes(search);
        const matchesAmount = !amount || remaining >= amount;

        return matchesSearch && matchesAmount;
    });

    const getDocumentSortValue = (document, key) => {
        switch (key) {
            case 'type':
                return document.paymentDocumentType || '';
            case 'numero':
                return document.numero || '';
            case 'date': {
                const date = document.dateFacture || document.dateReception || document.createdAt;
                return date ? new Date(date).getTime() || 0 : 0;
            }
            case 'total':
                return getDocumentTotalAmount(document);
            case 'paid':
                return getDocumentPaidAmount(document);
            case 'remaining':
                return getDocumentRemainingAmount(document);
            case 'status':
                return document.statut || document.status || '';
            default:
                return '';
        }
    };

    const sortedAvailableDocuments = [...filteredAvailableDocuments].sort((a, b) => {
        const left = getDocumentSortValue(a, documentSort.key);
        const right = getDocumentSortValue(b, documentSort.key);

        if (typeof left === 'number' && typeof right === 'number') {
            return documentSort.direction === 'asc' ? left - right : right - left;
        }

        return documentSort.direction === 'asc'
            ? String(left).localeCompare(String(right), undefined, { numeric: true, sensitivity: 'base' })
            : String(right).localeCompare(String(left), undefined, { numeric: true, sensitivity: 'base' });
    });
    const selectableVisibleDocuments = sortedAvailableDocuments.filter(document => getDocumentRemainingAmount(document) > 0);
    const allVisibleDocumentsSelected = selectableVisibleDocuments.length > 0
        && selectableVisibleDocuments.every(document => selectedDocuments.some(selected => selected.id === document.id));

    const selectAllVisibleDocuments = () => {
        if (!canEditDocuments) return;
        if (selectableVisibleDocuments.length === 0) return;
        setIsCautionPayment(false);
        setSelectedDocuments(previous => {
            const selectedIds = new Set(previous.map(document => document.id));
            return applyExistingPaymentBudget([
                ...previous,
                ...selectableVisibleDocuments
                    .filter(document => !selectedIds.has(document.id))
                    .map(document => ({ ...document, montantAAffecter: getDocumentRemainingAmount(document) })),
            ]);
        });
    };

    const handleDocumentSort = (key) => {
        setDocumentSort(prev => ({
            key,
            direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc',
        }));
    };

    const renderDocumentStatusBadge = (document) => {
        const status = document.statut || document.status || 'INCONNU';
        const color = ['PAYEE', 'REGLE', 'RECU', 'VALIDEE'].includes(status)
            ? 'success'
            : ['PARTIELLEMENT_PAYEE', 'PARTIEL', 'EN_COURS'].includes(status)
                ? 'warning'
                : ['ANNULEE', 'ANNULE', 'RETOUR'].includes(status)
                    ? 'danger'
                    : 'secondary';
        return <Badge color={color}>{String(status).replace(/_/g, ' ')}</Badge>;
    };

    const documentLoading = loadingInvoices || isLoading;

    useEffect(() => {
        if (!isEditing || documentsInitialized || !Array.isArray(availableDocuments) || availableDocuments.length === 0) {
            return;
        }

        const numeros = new Set([
            ...(reglement?.numFactures ? reglement.numFactures.split(',') : []),
            ...(reglement?.numBonReceptions ? reglement.numBonReceptions.split(',') : [])
        ].map((item) => normalizeNumero(item)));

        if (numeros.size === 0) {
            setDocumentsInitialized(true);
            return;
        }

        const existingDocuments = availableDocuments
            .filter((document) => numeros.has(normalizeNumero(document.numero)))
            .map((document) => {
                const allocatedInOperation = (Array.isArray(reglement?.allocations) ? reglement.allocations : [])
                    .filter(item =>
                        isAllocationOwnedByCurrentOperation(item) &&
                        normalizeNumero(item.numeroDocument) === normalizeNumero(document.numero)
                    )
                    .reduce((total, item) => total + Number(item.montantAlloue || 0), 0);
                return {
                    ...document,
                    montantAAffecter: allocatedInOperation
                };
            });

        setSelectedDocuments(existingDocuments);
        setDocumentsInitialized(true);
    }, [availableDocuments, documentsInitialized, isEditing, reglement]);

    return (
        <Container fluid className="supplier-payment-form-page payment-form-compact-page payment-form-modern">
            <Formik
                innerRef={formikRef}
                initialValues={initialValues}
                validationSchema={validationSchema}
                onSubmit={handleSubmit}
                enableReinitialize
            >
                {({ values, setFieldValue, isSubmitting, errors, touched, handleChange, handleBlur }) => (
                    <Form className="payment-form-shell">
                        <div className="supplier-payment-form-header payment-form-header">
                            <div>
                                <div className="supplier-payment-form-eyebrow payment-form-eyebrow">Paiement fournisseur</div>
                                <h2>{reglement ? t('reglement.edit') : t('reglement.new')}</h2>
                                <p>Selectionnez le fournisseur, consultez les impayes, puis affectez le paiement aux BR, factures ou avances.</p>
                            </div>
                            <div className="payment-header-actions">
                                <Badge color={reglement?.statut === 'DECAISSE' ? 'success' : reglement?.statut === 'ANNULE' ? 'danger' : 'info'}>
                                    {reglement?.statut || 'NOUVEAU'}
                                </Badge>
                                <Button
                                    color="secondary"
                                    outline
                                    size="sm"
                                    onClick={() => navigate(-1)}
                                    className="d-flex align-items-center"
                                >
                                    <FaArrowLeft className="me-2" />
                                    {t('common.back', 'Retour')}
                                </Button>
                                {canEdit && (
                                    <Button
                                        type="button"
                                        color="primary"
                                        size="sm"
                                        className="payment-primary-action"
                                        onClick={() => goToStep(4)}
                                        disabled={isSubmitting || !selectedFournisseur}
                                    >
                                        {isSubmitting && <Spinner size="sm" className="me-2" />}
                                        <i className={`fas ${reglement ? 'fa-save' : 'fa-plus'} me-1`}></i>
                                        Vérifier le paiement
                                    </Button>
                                )}
                            </div>
                        </div>
                        {reglement && (
                            <Alert color={canEdit ? "info" : "warning"} className="py-2 mb-2">
                                {canEdit
                                    ? "Vous pouvez modifier ce règlement tant qu'il n'est affecté à aucun document."
                                    : "Ce règlement fournisseur a déjà été affecté. Il est consultable, mais ne peut plus être modifié."}
                            </Alert>
                        )}
                        <GuidedPaymentStepper
                            activeStep={activeStep}
                            onStepChange={(step) => goToStep(paymentPurpose === 'ADVANCE' && step === 2 ? 3 : step)}
                        />
                        <div className="d-flex justify-content-between align-items-center mb-2">
                            <Button type="button" color="secondary" outline size="sm" disabled={activeStep === 1} onClick={() => goToStep(paymentPurpose === 'ADVANCE' && activeStep === 3 ? 1 : activeStep - 1)}>
                                Étape précédente
                            </Button>
                            {activeStep < 4 && (
                                <Button type="button" color="primary" size="sm" onClick={() => goToStep(paymentPurpose === 'ADVANCE' && activeStep === 1 ? 3 : activeStep + 1)}>
                                    Continuer
                                </Button>
                            )}
                        </div>
                        <div className={`payment-workspace payment-workspace-step-${activeStep}`}>
                        <section className="payment-main-pane">
                            {/* Section Client et Type */}
                            <Col md={12} className="payment-form-left-item">
                                <Card id="supplier-payment-step-1" className={`supplier-payment-form-panel payment-form-panel payment-form-identity-panel mb-3 ${activeStep === 1 ? '' : 'd-none'}`}>
                                    <CardHeader className="supplier-payment-form-section-header payment-form-section-header">
                                        <h5 className="mb-0">
                                            <FaMoneyBillWave className="me-2" />
                                            {t('reglement.informationsGenerales')}
                                        </h5>
                                    </CardHeader>
                                    <CardBody>
                                        <Row className="g-3 align-items-center">
                                            <Col md={12}>
                                                <Label className="fw-bold text-secondary mb-2">Objet du paiement *</Label>
                                                <div className="d-flex flex-wrap gap-2">
                                                    <Button type="button" color={paymentPurpose === 'DOCUMENTS' ? 'primary' : 'secondary'} outline={paymentPurpose !== 'DOCUMENTS'} onClick={() => {
                                                        setPaymentPurpose('DOCUMENTS');
                                                        if (typeReglement === 'AVANCE') setTypeReglement('FACTURE');
	                                                    }} disabled={!canEdit || isQuickPayment}>
                                                        Régler des documents
                                                    </Button>
                                                    <Button type="button" color={paymentPurpose === 'ADVANCE' ? 'success' : 'secondary'} outline={paymentPurpose !== 'ADVANCE'} onClick={() => {
                                                        setPaymentPurpose('ADVANCE');
                                                        setTypeReglement('AVANCE');
                                                        setSelectedDocuments([]);
                                                        setIsCautionPayment(false);
	                                                    }} disabled={!canEdit || isQuickPayment}>
                                                        Enregistrer une avance
                                                    </Button>
                                                </div>
                                                {paymentPurpose === 'ADVANCE' && <div className="small text-muted mt-2">
                                                    Le paiement sera enregistré comme avance fournisseur disponible, sans document lié et sans créer un second règlement.
                                                </div>}
		                            </Col>
                                            <Col md={6}>
                                                <FormGroup>
                                                    <Label for="companyId" className="fw-bold text-secondary">
                                                        {t('company.company', 'Entreprise')} *
                                                    </Label>
                                                    <Input
                                                        type="select"
                                                        id="companyId"
                                                        name="companyId"
                                                        value={values.companyId || ''}
                                                        onChange={(e) => {
                                                            handleChange(e);
                                                            setSelectedCompanyId(e.target.value ? Number(e.target.value) : null);
                                                            setSelectedDocuments([]);
                                                            setIsCautionPayment(false);
                                                            setDocumentFilters({ query: '', amount: '' });
                                                            setDocumentsInitialized(false);
                                                        }}
                                                        onBlur={handleBlur}
                                                        invalid={touched.companyId && errors.companyId}
                                                        disabled={!canEdit || isQuickPayment || (isEditing && isDocumentBoundType)}
                                                    >
                                                        <option value="">{t('company.selectCompany', 'Sélectionner une entreprise')}</option>
                                                        {companies?.map((item) => (
                                                            <option key={item.id} value={item.id}>
                                                                {item.name}
                                                            </option>
                                                        ))}
                                                    </Input>
                                                    {touched.companyId && errors.companyId && (
                                                        <div className="invalid-feedback d-block">{errors.companyId}</div>
                                                    )}
                                                </FormGroup>
                                            </Col>
                                            <Col md={6}>
                                                <FormGroup>
                                                    <Label for="fournisseurId" className="fw-bold text-secondary">
                                                        <FaUser className="me-1" />
                                                        {t('reglement.fournisseur')} *
                                                    </Label>
                                                    <Select
                                                        options={fournisseurOptions}
                                                        value={selectedFournisseur}
                                                        onChange={(option) => {
                                                            setSelectedFournisseur(option);
                                                            setFieldValue('fournisseurId', option?.value || '');
                                                            setSelectedDocuments([]);
                                                            setIsCautionPayment(false);
                                                            setDocumentFilters({ query: '', amount: '' });
                                                            setDocumentsInitialized(false);
                                                            if (option) {
                                                                dispatch(reglementsFournisseurActions.echeanceFournisseur({ ...filters, fournisseurId: option.value }));
                                                                dispatch(reglementsFournisseurActions.cautionFournisseur({ ...filters, fournisseurId: option.value }));
                                                                setFilters(f => ({ ...f, fournisseurId: option.value }));
                                                            }
                                                        }}
                                                        placeholder={t('reglement.selectFournisseur')}
                                                        isClearable={!reglement && !isQuickPayment}
                                                        isSearchable={!reglement && !isQuickPayment}
                                                        isDisabled={!canEdit || isEditing || isQuickPayment}
                                                        className={`rounded-3 border border-2 ${errors.fournisseurId && touched.fournisseurId ? 'is-invalid' : ''}`}
                                                        menuPortalTarget={typeof window !== 'undefined' ? document.body : null}
                                                        styles={{
                                                            menuPortal: base => ({ ...base, zIndex: 9999 })
                                                        }}
                                                    />
                                                    <ErrorMessage name="fournisseurId" component="div" className="text-danger small mt-1" />
                                                </FormGroup>
                                            </Col>
                                            <Col md={3} className="d-none">
                                                <FormGroup>
                                                    <Label className="fw-bold text-secondary">{t('reglement.typeReglement')}</Label>
                                                    <Select
                                                        options={typeReglementOptions}
                                                        value={typeReglementOptions.find(option => option.value === typeReglement)}
                                                        onChange={(option) => {
                                                            setTypeReglement(option.value);
                                                            setSelectedDocuments([]);
                                                            setDocumentFilters({ query: '', amount: '' });
                                                            setDocumentsInitialized(false);
                                                        }}
                                                        placeholder={t('reglement.selectTypeReglement', 'Sélectionner le type')}
                                                        isDisabled={!canEdit || isEditing || isQuickPayment}
                                                        className="rounded-3 border border-2"
                                                        menuPortalTarget={typeof window !== 'undefined' ? document.body : null}
                                                        styles={{
                                                            menuPortal: base => ({ ...base, zIndex: 9999 }),
                                                            option: (provided, state) => ({
                                                                ...provided,
                                                                display: 'flex',
                                                                alignItems: 'center'
                                                            })
                                                        }}
                                                        formatOptionLabel={(option) => (
                                                            <div className="d-flex align-items-center">
                                                                <option.icon className={`me-2 ${option.value === 'FACTURE' ? 'text-primary' :
                                                                    option.value === 'BON_RECEPTION' ? 'text-success' :
                                                                        'text-warning'
                                                                    }`} />
                                                                {option.label}
                                                            </div>
                                                        )}
                                                    />
                                                </FormGroup>
                                            </Col>
                                            {/* {typeReglement === "AVANCE" && (
                                                <Col md={3}>
                                                    <FormGroup>
                                                        <Label className="fw-bold text-secondary">
                                                            {t('reglement.documentsARegler')}
                                                        </Label>

                                                        <Select
                                                            options={avanceDocFilterOptions}
                                                            value={avanceDocFilterOptions.find(o => o.value === avanceDocFilter)}
                                                            onChange={(opt) => {
                                                                setAvanceDocFilter(opt.value);
                                                                setSelectedDocuments([]);
                                                                if (selectedFournisseur) {
                                                                    // Trigger reload
                                                                    if (opt.value !== avanceDocFilter) {
                                                                        // force useEffect reload
                                                                    }
                                                                }
                                                            }}
                                                            className="rounded-3 border border-2"
                                                        />
                                                    </FormGroup>
                                                </Col>
                                            )} */}
                                            <Col md={3} className="d-none">
                                                <FormGroup>
                                                    <Label for="dateReglement" className="fw-bold text-secondary">
                                                        <FaCalendarAlt className="me-1" />
                                                        {t('reglement.dateReglement')} *
                                                    </Label>
                                                    <Field
                                                        name="dateReglement"
                                                        type="date"
                                                        className={`form-control rounded-3 border border-2 ${errors.dateReglement && touched.dateReglement ? 'is-invalid' : ''}`}
                                                        disabled={!canEdit}
                                                    />
                                                    <ErrorMessage name="dateReglement" component="div" className="invalid-feedback" />
                                                </FormGroup>
                                            </Col>
                                            <Col md={3} className="d-none">
                                                <FormGroup>
                                                    <Label for="dateDecaissement" className="fw-bold text-secondary">
                                                        <FaCalendarAlt className="me-1" />
                                                        {t('reglement.dateDecaissement', 'Date décaissement')}
                                                    </Label>
                                                    <Field
                                                        name="dateDecaissement"
                                                        type="date"
                                                        className="form-control rounded-3 border border-2"
                                                        disabled={!canEdit}
                                                    />
                                                </FormGroup>
                                            </Col>
                                        </Row>
                                        {selectedFournisseur && (
                                            <Row className="mt-2 g-2">
                                                <Col>
                                                    <Row className="g-2 align-items-stretch">
                                                        <Col xs={6} md={2}>
                                                            <Card className="shadow-sm h-100 bg-light border-0 rounded-2">
                                                                <CardBody className="d-flex flex-column justify-content-center align-items-center py-2">
                                                                    <FaBalanceScale className="mb-1 text-success" size={20} />
                                                                    <span className="text-muted text-uppercase" style={{ fontSize: '0.7rem' }}>{t('client.balance')}</span>
                                                                    {selectedFournisseur.totalCredit > selectedFournisseur.totalDebit ? (
                                                                        <span className="fw-bold text-success" style={{ fontSize: '0.9rem' }}>+{formatPrice(Math.abs(selectedFournisseur.solde))}</span>
                                                                    ) : selectedFournisseur.totalCredit < selectedFournisseur.totalDebit ? (
                                                                        <span className="fw-bold text-danger" style={{ fontSize: '0.9rem' }}>-{formatPrice(Math.abs(selectedFournisseur.solde))}</span>
                                                                    ) : (
                                                                        <span className="fw-bold text-muted" style={{ fontSize: '0.9rem' }}>{formatPrice(0)}</span>
                                                                    )}
                                                                </CardBody>
		                                </Card>
		                            </Col>
                                                        <Col xs={6} md={2}>
                                                            <Card className="shadow-sm h-100 bg-light border-0 rounded-2">
                                                                <CardBody className="d-flex flex-column justify-content-center align-items-center py-2">
                                                                    <FaArrowDown className="mb-1 text-danger" size={20} />
                                                                    <span className="text-muted text-uppercase" style={{ fontSize: '0.7rem' }}>{t('client.totalDebit')}</span>
                                                                    <span className="fw-bold" style={{ fontSize: '0.9rem' }}>{formatPrice(selectedFournisseur.totalDebit)}</span>
                                                                </CardBody>
                                                            </Card>
                                                        </Col>
                                                        <Col xs={6} md={2}>
                                                            <Card className="shadow-sm h-100 bg-light border-0 rounded-2">
                                                                <CardBody className="d-flex flex-column justify-content-center align-items-center py-2">
                                                                    <FaArrowUp className="mb-1 text-primary" size={20} />
                                                                    <span className="text-muted text-uppercase" style={{ fontSize: '0.7rem' }}>{t('client.totalCredit')}</span>
                                                                    <span className="fw-bold" style={{ fontSize: '0.9rem' }}>{formatPrice(selectedFournisseur.totalCredit)}</span>
                                                                </CardBody>
                                                            </Card>
                                                        </Col>
                                                        <Col xs={6} md={3}>
                                                            <Card className="shadow-sm h-100 bg-warning bg-opacity-10 border-0 rounded-2">
                                                                <CardBody className="d-flex flex-column justify-content-center align-items-center py-2">
                                                                    <div className="d-flex align-items-center mb-1">
                                                                        <FaCalendarAlt className="me-1 text-warning" size={16} />
                                                                        <span className="text-muted text-uppercase" style={{ fontSize: '0.7rem' }}>{t('client.echeances')}</span>
                                                                    </div>
                                                                    <span className="fw-bold text-warning" style={{ fontSize: '0.9rem' }}>{echeances?.length || 0}</span>
                                                                    <Button color="link" size="sm" className="p-0 fw-bold text-warning" style={{ fontSize: '0.7rem' }} onClick={() => setShowEcheanceModal(true)}>
                                                                        {t('client.viewDetails')}
                                                                    </Button>
                                                                </CardBody>
                                                            </Card>
                                                        </Col>
                                                        <Col xs={6} md={3}>
                                                            <Card className="shadow-sm h-100 bg-info bg-opacity-10 border-0 rounded-2">
                                                                <CardBody className="d-flex flex-column justify-content-center align-items-center py-2">
                                                                    <div className="d-flex align-items-center mb-1">
                                                                        <FaShieldAlt className="me-1 text-info" size={16} />
                                                                        <span className="text-muted text-uppercase" style={{ fontSize: '0.7rem' }}>{t('client.cautions')}</span>
                                                                    </div>
                                                                    <span className="fw-bold text-info" style={{ fontSize: '0.9rem' }}>{cautions?.length || 0}</span>
                                                                    <Button color="link" size="sm" className="p-0 fw-bold text-info" style={{ fontSize: '0.7rem' }} onClick={() => setShowCautionModal(true)}>
                                                                        {t('client.viewDetails')}
                                                                    </Button>
                                                                </CardBody>
                                                            </Card>
                                                        </Col>
                                                    </Row>
                                                </Col>
                                            </Row>
                                        )}
                                    </CardBody>
                                </Card>
                            </Col>

                       

                            {/* Section Détails du règlement */}
                            <Col md={12} className="payment-form-left-item payment-form-documents-item">
                                {paymentPurpose === 'DOCUMENTS' && (
                                <Card id="supplier-payment-step-2" className={`supplier-payment-form-panel payment-form-panel mb-3 ${activeStep === 2 ? '' : 'd-none'}`}>
                                    <CardHeader className="supplier-payment-form-section-header payment-form-section-header">
                                        <div className="d-flex justify-content-between align-items-center">
                                            <h5 className="mb-0">
                                                <FaList className="me-2 text-primary" />
                                                {t('reglement.documentsARegler')}
                                            </h5>
                                            {false && typeReglement === "AVANCE" && (
                                                <div style={{ minWidth: 220 }}>
                                                    <Select
                                                        options={avanceDocFilterOptions}
                                                        value={avanceDocFilterOptions.find(option => option.value === avanceDocFilter)}
                                                        onChange={(option) => {
                                                            setAvanceDocFilter(option.value);
                                                            setSelectedDocuments([]);
                                                            setDocumentFilters({ query: '', amount: '' });
                                                        }}
                                                        isDisabled={!canEdit}
                                                        styles={compactSelectStyles}
                                                    />
                                                </div>
                                            )}
                                        </div>
                                    </CardHeader>
                                    <CardBody>
                                        <PaymentHelp>Sélectionnez les documents à payer. Leur solde sera affecté automatiquement et le montant du règlement sera calculé par le système.</PaymentHelp>
                                        <Row className="g-2 mb-3 align-items-end">
                                            <Col md={4}>
                                                <FormGroup className="mb-0">
                                                    <Label className="mb-1">{t('common.search', 'Rechercher')}</Label>
                                                    <div className="input-group input-group-sm">
                                                        <span className="input-group-text">
                                                            <FaSearch />
                                                        </span>
                                                        <Input
                                                            value={documentFilters.query}
                                                            placeholder={t('reglement.searchDocument', 'N° document, référence, fournisseur')}
                                                            onChange={(e) => setDocumentFilters(prev => ({ ...prev, query: e.target.value }))}
                                                            disabled={!selectedFournisseur || !selectedCompanyId || !canEditDocuments}
                                                        />
                                                    </div>
                                                </FormGroup>
                                            </Col>
                                            <Col md={3}>
                                                <FormGroup className="mb-0">
                                                    <Label className="mb-1">{t('common.amount', 'Montant')}</Label>
                                                    <Input
                                                        type="number"
                                                        min="0"
                                                        step="0.01"
                                                        value={documentFilters.amount}
                                                        placeholder={t('reglement.amountFilterPlaceholder', 'Montant restant min')}
                                                        onChange={(e) => setDocumentFilters(prev => ({ ...prev, amount: e.target.value }))}
                                                        disabled={!selectedFournisseur || !selectedCompanyId || !canEditDocuments}
                                                    />
                                                </FormGroup>
                                            </Col>
                                            <Col md={5}>
                                                <div className="d-flex justify-content-md-end gap-3 small text-muted pt-4">
                                                    <span>{filteredAvailableDocuments.length} {t('reglement.documents', 'documents')}</span>
                                                    <span>{t('reglement.total')}: {formatPrice(filteredAvailableDocuments.reduce((sum, document) => sum + getDocumentRemainingAmount(document), 0), false)} DH</span>
                                                </div>
                                            </Col>
                                        </Row>

                                        {!selectedCompanyId ? (
                                            <div className="text-muted text-center py-3 border rounded mb-3">
                                                {t('company.selectCompanyFirst', 'Sélectionnez une entreprise pour afficher les documents')}
                                            </div>
                                        ) : !selectedFournisseur ? (
                                            <div className="text-muted text-center py-3 border rounded mb-3">
                                                {t('reglement.selectFournisseurFirst', 'Sélectionnez un fournisseur pour afficher les documents')}
                                            </div>
                                        ) : documentLoading ? (
                                            <div className="text-center py-4 border rounded mb-3">
                                                <Spinner color="primary" size="sm" />
                                                <div className="mt-2 text-muted">{t('common.loading')}</div>
                                            </div>
                                        ) : (
	                                            <div className="payment-document-card-section mb-2">
                                                <div className="payment-document-sortbar">
                                                    <span>Trier par</span>
                                                    {[
                                                        ['numero', 'Document'],
                                                        ['total', 'Montant'],
                                                        ['remaining', 'Reste à régler'],
                                                    ].map(([key, label]) => (
                                                        <Button key={key} type="button" size="sm" color={documentSort.key === key ? 'primary' : 'light'} onClick={() => handleDocumentSort(key)}>
                                                            {label}{documentSort.key === key ? (documentSort.direction === 'asc' ? ' ↑' : ' ↓') : ''}
                                                        </Button>
                                                    ))}
                                                    <Button
                                                        type="button"
                                                        size="sm"
                                                        color="success"
                                                        className="ms-auto"
                                                        onClick={selectAllVisibleDocuments}
                                                        disabled={!canEditDocuments || selectableVisibleDocuments.length === 0 || allVisibleDocumentsSelected}
                                                    >
                                                        Tout sélectionner
                                                    </Button>
                                                </div>
                                                {filteredAvailableDocuments.length === 0 ? (
                                                    <div className="payment-document-empty">{t('reglement.noDocumentsFound', 'Aucun document trouvé')}</div>
                                                ) : (
                                                    <div className="payment-document-card-grid">
                                                        {sortedAvailableDocuments.map((document) => {
                                                            const montantRestant = getDocumentRemainingAmount(document);
                                                            const isSelected = selectedDocuments.some(doc => doc.id === document.id);
                                                            const isQuickDocument = quickDocuments.some(item => Number(item.id) === Number(document.id));

                                                            return (
                                                                <article key={document.id} className={`payment-document-card ${isSelected ? 'is-selected' : ''} ${isQuickDocument ? 'is-suggested' : ''}`}>
                                                                    <header className="payment-document-card-header">
                                                                        <div className="payment-document-select">
                                                                            <Input
                                                                                type="checkbox"
                                                                                checked={isSelected}
                                                                                disabled={!canEditDocuments || montantRestant <= 0}
                                                                                onChange={(event) => toggleDocumentSelection(document, event.target.checked)}
                                                                                aria-label={`Sélectionner ${document.numero}`}
                                                                            />
                                                                            <div>
                                                                                <div className="payment-document-number">{document.numero}</div>
	                                                                                <div className="payment-document-meta">
	                                                                                    {document.paymentDocumentType === 'FACTURE' ? 'FACT' : 'BR'}
	                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                        <div className="d-flex align-items-center gap-2">
                                                                            {isQuickDocument && <Badge color="warning">Suggéré</Badge>}
                                                                            {renderDocumentStatusBadge(document)}
                                                                        </div>
                                                                    </header>
	                                                                    <div className="payment-document-metrics">
	                                                                        <div><span>Date</span><strong>{document.dateFacture || document.dateReception || document.createdAt || '-'}</strong></div>
                                                                        <div><span>Retours / avoirs</span><strong>{formatPrice(getDocumentReturnAmount(document), false)} DH</strong></div>
                                                                        <div><span>Montant net</span><strong>{formatPrice(getDocumentNetAmount(document), false)} DH</strong></div>
                                                                    </div>
                                                                    <div className="payment-document-allocation">
                                                                        <div className="payment-document-remaining">
                                                                            <span>Reste avant ce règlement</span>
                                                                            <strong>{formatPrice(montantRestant, false)} DH</strong>
                                                                        </div>
                                                                    </div>
                                                                </article>
                                                            );
                                                        })}
                                                    </div>
                                                )}
                                            </div>
                                        )}

	                                        <div className="payment-documents-summary border rounded p-3 bg-light-subtle mb-2">
                                            <div className="d-flex flex-wrap justify-content-between gap-3">
                                                <div>
                                                    <div className="small text-muted text-uppercase">
                                                        {t('reglement.documentsSelectionnes', 'Documents sélectionnés')}
                                                    </div>
                                                    <div className="fw-bold fs-5">{selectedDocuments.length}</div>
                                                </div>
                                                <div className="text-md-end">
                                                    <div className="small text-muted text-uppercase">
                                                        {t('reglement.total', 'Total')}
                                                    </div>
                                                    <div className="fw-bold fs-5 text-primary">
                                                        {formatPrice(calculateTotalAmount(), false)} DH
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
	                                    </CardBody>
		                                </Card>
                                )}
		                            </Col>
                                    {activeStep === 2 && (
                                        <PaymentFinancialSummary
                                            paymentAmount={values.montantRegle}
                                            allocatedAmount={calculateTotalAmount()}
                                            currentPaymentAllocated={calculateCurrentPaymentAllocation(selectedDocuments)}
                                            advanceAmount={calculateAdvanceUsageAmount(selectedDocuments)}
                                            selectedCount={selectedDocuments.length}
                                            partyKind="fournisseur"
                                        />
                                    )}
                        </section>
                        <aside className="payment-side-pane">

                            {selectedFournisseur && (
                                <Col md={12} className={`payment-form-right-item ${activeStep === 3 ? '' : 'd-none'}`}>
                                    <Alert color={Number(values.montantRegle || 0) + calculateAdvanceUsageAmount(selectedDocuments) >= calculateTotalAmount() ? 'info' : 'warning'} className="mb-3">
                                        <div className="fw-bold mb-1">Situation avant paiement</div>
                                        <div className="small">
                                            {selectedDocuments.length} document(s) selectionne(s),
                                            reste a payer {formatPrice(calculateTotalAmount(), false)} DH.
                                            {loadingAdvances ? (
                                                <span className="ms-1">Verification des avances...</span>
                                            ) : totalAvailableAdvances > 0 && (
                                                <span className="text-success ms-1">
                                                    Avances disponibles {formatPrice(totalAvailableAdvances, false)} DH,
                                                    selectionnees {formatPrice(calculateAdvanceUsageAmount(selectedDocuments), false)} DH.
                                                </span>
                                            )}
                                            Montant saisi {formatPrice(Number(values.montantRegle || 0), false)} DH.
                                            {Number(values.montantRegle || 0) + calculateAdvanceUsageAmount(selectedDocuments) > calculateTotalAmount() && (
                                                <span className="text-success ms-1">
                                                    Le surplus de {formatPrice(Number(values.montantRegle || 0) + calculateAdvanceUsageAmount(selectedDocuments) - calculateTotalAmount(), false)} DH restera en {isCautionPayment ? 'caution fournisseur' : 'avance fournisseur'}.
                                                </span>
                                            )}
                                            {Number(values.montantRegle || 0) + calculateAdvanceUsageAmount(selectedDocuments) < calculateTotalAmount() && (
                                                <span className="text-warning ms-1">
                                                    Paiement partiel: {formatPrice(calculateTotalAmount() - Number(values.montantRegle || 0) - calculateAdvanceUsageAmount(selectedDocuments), false)} DH restera impaye.
                                                </span>
                                            )}
                                        </div>
                                    </Alert>
                                </Col>
                            )}

	                            <Col md={12} className={`payment-form-right-item ${activeStep === 3 ? '' : 'd-none'}`}>
	                                {selectedFournisseur && selectedDocuments.length > 0 && externalAdvances.length > 0 && (
                                    <Card className="supplier-payment-form-panel payment-form-panel mb-3">
                                        <CardHeader className="supplier-payment-form-section-header payment-form-section-header py-2">
                                            <h6 className="mb-0">{t('reglement.avancesDisponibles', 'Avances disponibles')}</h6>
                                        </CardHeader>
                                        <CardBody className="py-2">
                                            <div className="small text-muted mb-2">
                                                {t('reglement.selectAvancesToUse', 'Cochez les avances à utiliser pour ces documents.')}
                                            </div>
                                            <div className="table-responsive">
                                                <Table size="sm" className="mb-0 align-middle">
                                                    <tbody>
	                                                        {externalAdvances.map((avance) => {
                                                            const checked = selectedAdvanceIds.includes(avance.id);
                                                            return (
                                                                <tr key={avance.id}>
                                                                    <td style={{ width: 34 }}>
                                                                        <Input
                                                                            type="checkbox"
                                                                            checked={checked}
	                                                                            disabled={!canEdit}
	                                                                            onChange={(event) => {
	                                                                                const selected = event.target.checked;
	                                                                                setSelectedAdvanceIds(prev => selected
	                                                                                    ? (prev.includes(avance.id) ? prev : [...prev, avance.id])
	                                                                                    : prev.filter(id => id !== avance.id));
	                                                                            }}
                                                                        />
                                                                    </td>
                                                                    <td>
                                                                        <div className="fw-bold">{avance.referenceReglement || `Avance #${avance.id}`}</div>
	                                                                        <div className="text-muted small">{avance.dateCreation || '-'} · origine {formatPrice(avance.montantAvance || 0, false)} DH · utilisé {formatPrice(avance.montantUtilise || 0, false)} DH</div>
                                                                    </td>
	                                                                    <td className="text-end fw-bold text-success">
	                                                                        {formatPrice(avance.montantDisponible, false)} DH
	                                                                    </td>
                                                                </tr>
                                                            );
                                                        })}
                                                    </tbody>
                                                </Table>
                                            </div>
                                        </CardBody>
                                    </Card>
                                )}
                                <Card id="supplier-payment-step-3" className="supplier-payment-form-panel payment-form-panel mb-3">
                                    <CardHeader className="supplier-payment-form-section-header payment-form-section-header">
                                        <h5 className="mb-0">
                                            <FaEdit className="me-2" />
                                            {t('reglement.detailsReglement')}
                                        </h5>
                                    </CardHeader>
                                    <CardBody>
                                        <Row className="g-2 payment-form-details-grid">
                                            <Col md={12}>
                                                <FormGroup className="payment-amount-control">
                                                    {paymentPurpose === 'DOCUMENTS' && selectedDocuments.length > 0 && totalAvailableAdvances > 0 && (
                                                        <div className={`available-advance-callout ${selectedAdvanceIds.length > 0 ? 'is-applied' : ''}`} role="status">
                                                            <div>
                                                                <strong>
                                                                    {selectedAdvanceIds.length > 0 ? 'Avances prises en compte' : 'Des avances sont disponibles'}
                                                                </strong>
                                                                <span>
                                                                    {formatPrice(totalAvailableAdvances, false)} DH disponibles · {formatPrice(calculateAdvanceUsageAmount(selectedDocuments), false)} DH appliqués
                                                                </span>
                                                            </div>
                                                            <Button
                                                                type="button"
                                                                color={selectedAdvanceIds.length > 0 ? 'outline-success' : 'success'}
                                                                size="sm"
                                                                disabled={!canEdit}
	                                                                onClick={() => {
	                                                                    advancePreferenceInitializedRef.current = true;
	                                                                    applyAvailableAdvancesFirst(setFieldValue);
	                                                                }}
                                                            >
                                                                {selectedAdvanceIds.length > 0 ? 'Recalculer avec les avances' : 'Utiliser les avances d’abord'}
                                                            </Button>
                                                        </div>
                                                    )}
                                                    <Label for="montantRegle">
                                                        Montant réellement payé *
                                                    </Label>
                                                    <Field
                                                        name="montantRegle"
                                                        type="number"
                                                        onFocus={(e) => {
                                                            e.target.select();
                                                            e.target.addEventListener('wheel', preventScroll, { passive: false });
                                                        }}
                                                        onBlur={(e) => {
                                                            e.target.removeEventListener('wheel', preventScroll);
                                                        }}

                                                        className={`form-control ${errors.montantRegle && touched.montantRegle ? 'is-invalid' : ''}`}
                                                        placeholder="0.00"
                                                        value={values.montantRegle}
                                                        onChange={(e) => {
                                                            setFieldValue('montantRegle', e.target.value);
                                                            if (paymentPurpose === 'DOCUMENTS') {
                                                                setSelectedDocuments(previous => allocateDocumentsAutomatically(
                                                                    previous,
                                                                    Number(e.target.value || 0) + calculateSelectedAdvanceCapacity()
                                                                ));
                                                            }
                                                            
                                                        }}
                                                        disabled={!canEdit}
                                                        
                                                    />
                                                    <ErrorMessage name="montantRegle" component="div" className="invalid-feedback" />
                                                    <div className="payment-form-field-hint">
                                                        {paymentPurpose === 'DOCUMENTS' && selectedDocuments.length > 0
                                                            ? 'Le système répartit automatiquement ce montant sur les documents sélectionnés. Tout surplus sera conservé comme avance fournisseur.'
                                                            : 'Saisissez le montant réellement payé au fournisseur.'}
                                                    </div>
                                                </FormGroup>
                                            </Col>
                                            <Col md={6}>
                                                <FormGroup>
                                                    <Label for="dateReglement">
                                                        <FaCalendarAlt className="me-1" />
                                                        {t('reglement.dateReglement')} *
                                                    </Label>
                                                    <Field
                                                        name="dateReglement"
                                                        type="date"
                                                        className={`form-control ${errors.dateReglement && touched.dateReglement ? 'is-invalid' : ''}`}
                                                        disabled={!canEdit}
                                                    />
                                                    <ErrorMessage name="dateReglement" component="div" className="invalid-feedback" />
                                                </FormGroup>
                                            </Col>
                                            <Col md={6}>
                                                <FormGroup>
                                                    <Label for="dateDecaissement">
                                                        <FaCalendarAlt className="me-1" />
                                                        {t('reglement.dateDecaissement', 'Date décaissement')}
                                                    </Label>
                                                    <Field
                                                        name="dateDecaissement"
                                                        type="date"
                                                        className="form-control"
                                                        disabled={!canEdit}
                                                    />
                                                </FormGroup>
                                            </Col>
                                            {selectedDocuments.length === 0 && (
                                                <Col md={12}>
                                                    <FormGroup check className="payment-caution-option">
                                                        <Input
                                                            id="isSupplierCautionPayment"
                                                            type="checkbox"
                                                            checked={isCautionPayment}
                                                            disabled={!canEdit}
                                                            onChange={(e) => {
                                                                const checked = e.target.checked;
                                                                setIsCautionPayment(checked);
                                                                setTypeReglement(checked ? 'CAUTION' : 'AVANCE');
                                                                if (checked) {
                                                                    setSelectedDocuments([]);
                                                                    setDocumentFilters({ query: '', amount: '' });
                                                                }
                                                            }}
                                                        />
                                                        <Label check for="isSupplierCautionPayment">
                                                            {t('reglement.caution', 'Caution')}
                                                        </Label>
                                                        <div className="payment-form-field-hint">
                                                            Sans document selectionne, le paiement est une avance par defaut. Cochez pour le saisir comme caution.
                                                        </div>
                                                    </FormGroup>
                                                </Col>
                                            )}
                                            <Col md={4}>
                                                <FormGroup>
                                                    <Label for="modeReglement">{t('reglement.modeReglement')} *</Label>
                                                    <Field
                                                        as="select"
                                                        name="modeReglement"
                                                        className={`form-control ${errors.modeReglement && touched.modeReglement ? 'is-invalid' : ''}`}
                                                        disabled={!canEdit}
                                                    >
                                                        <option value="">{t('reglement.selectModeReglement')}</option>
                                                        {modeReglementOptions.map(option => (
                                                            <option key={option.value} value={option.value}>
                                                                {option.label}
                                                            </option>
                                                        ))}
                                                    </Field>
                                                    <ErrorMessage name="modeReglement" component="div" className="invalid-feedback" />
                                                </FormGroup>
                                            </Col>
                                            {(['CHEQUE', 'TRAITE'].includes(values.modeReglement)) && (

                                                <>
                                                    <Col md={4}>
                                                        <FormGroup>
                                                            <Label for="numeroCheque">
                                                                {values.modeReglement === 'TRAITE'
                                                                    ? t('reglement.numeroTraite', 'Numero traite')
                                                                    : t('reglement.numeroCheque')}
                                                            </Label>
                                                            <Field
                                                                name="numeroCheque"
                                                                type="text"
                                                                className="form-control"
                                                                placeholder={values.modeReglement === 'TRAITE'
                                                                    ? t('reglement.numeroTraite', 'Numero traite')
                                                                    : t('reglement.numeroCheque')}
                                                                disabled={!canEdit}
                                                            />
                                                        </FormGroup>
                                                    </Col>
                                                    <Col md={4}>
                                                        <FormGroup>
                                                            <Label for="banque">{t('reglement.banque')}</Label>
                                                            <Input
                                                                type="select"
                                                                id="banque"
                                                                name="banque"
                                                                value={values.banque}
                                                                onChange={handleChange}
                                                                onBlur={handleBlur}
                                                                invalid={touched.banque && errors.banque}
                                                                disabled={!canEdit}
                                                            >
                                                                <option value="">{t('payment.selectBank', 'Sélectionner une banque')}</option>
                                                                <option value="BMCE">BMCE</option>
                                                                <option value="ATTIJARI">ATTIJARI</option>
                                                                <option value="CIH">CIH</option>
                                                                <option value="CREDIT DU MAROC">CREDIT DU MAROC</option>
                                                                <option value="SG">SG</option>
                                                            </Input>
                                                        </FormGroup>
                                                    </Col>
                                                    <Col md={4}>
                                                        <FormGroup>
                                                            <Label for="dateEcheance">
                                                                <FaCalendarAlt className="me-1" />
                                                                {t('reglement.dateEcheance')}
                                                            </Label>
                                                            <Field
                                                                name="dateEcheance"
                                                                type="date"
                                                                className="form-control"
                                                                disabled={!canEdit}
                                                            />
                                                        </FormGroup>
                                                    </Col>
                                                </>

                                            )}
                                        </Row>

                                        {/* Champs conditionnels selon le mode de règlement */}

                                        {(values.modeReglement === 'VIREMENT') && (
                                            <Row className="mt-3">
                                                <Col md={12}>
                                                    <FormGroup>
                                                        <Label for="referencePaiement">{t('reglement.referencePaiement')}</Label>
                                                        <Field
                                                            name="referencePaiement"
                                                            type="text"
                                                            className="form-control"
                                                            disabled={!canEdit}
                                                        />
                                                    </FormGroup>
                                                </Col>
                                            </Row>
                                        )}

                                        <Row className="mt-3">
                                            <Col md={12}>
                                                <FormGroup>
                                                    <Label for="commentaire">{t('reglement.commentaire')}</Label>
                                                    <Field
                                                        as="textarea"
                                                        name="observations"
                                                        rows="3"
                                                        className="form-control"
                                                        placeholder={t('reglement.commentairePlaceholder')}
                                                        disabled={!canEdit}
                                                    />
                                                </FormGroup>
                                            </Col>
                                        </Row>


                                        {/* Allocations Table (Edit Mode) */}
                                        {reglement && Array.isArray(reglement.allocations) && reglement.allocations.length > 0 && (
                                            <Card className="supplier-payment-form-subpanel mt-3">
                                                <CardHeader className="supplier-payment-form-section-header py-2">
                                                    <h6 className="mb-0">{t('reglement.allocationsPayments', 'Affectations du règlement')}</h6>
                                                </CardHeader>
                                                <CardBody>
                                                    <div className="table-responsive">
                                                        <Table bordered hover size="sm">
                                                            <thead className="table-info">
                                                                <tr>
                                                                    <th>{t('reglement.documentType', 'Type')}</th>
                                                                    <th>{t('reglement.documentNumber', 'N° Document')}</th>
                                                                    <th>{t('reglement.montantDocument', 'Montant Document')}</th>
                                                                    <th>{t('reglement.montantAlloue', 'Montant Alloué')}</th>
                                                                    <th>{t('reglement.montantRestant', 'Montant Restant')}</th>
                                                                    <th>{t('reglement.commentaire', 'Commentaire')}</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {reglement.allocations.map(allocation => (
                                                                    <tr key={allocation.id}>
                                                                        <td>
                                                                            <div>{allocation.typeDocument}</div>
                                                                            <small className="text-muted">
                                                                                {allocation.sourcePaiement === 'AVANCE' ? 'Avance' : 'Paiement'}
                                                                                {' '}
                                                                                {allocation.referenceSourcePaiement || allocation.refReglement || ''}
                                                                            </small>
                                                                        </td>
                                                                        <td>{allocation.numeroDocument}</td>
                                                                        <td>{formatPrice(allocation.montantDocument)}</td>
                                                                        <td className="fw-bold text-success">{formatPrice(allocation.montantAlloue)}</td>
                                                                        <td className="fw-bold text-danger">{formatPrice(allocation.montantRestant)}</td>
                                                                        <td>{allocation.commentaire || '-'}</td>
                                                                    </tr>
                                                                ))}
                                                            </tbody>
                                                            <tfoot>
                                                                <tr>
                                                                    <td colSpan={2} className="fw-bold text-end">{t('reglement.total', 'Total')}:</td>
                                                                    <td className="fw-bold">{formatPrice(reglement.allocations.reduce((acc, allocation) => acc + allocation.montantDocument, 0))}</td>
                                                                    <td className="fw-bold text-success">{formatPrice(reglement.allocations.reduce((acc, allocation) => acc + allocation.montantAlloue, 0))}</td>
                                                                    <td className="fw-bold text-danger">{formatPrice(reglement.allocations.reduce((acc, allocation) => acc + allocation.montantRestant, 0))}</td>
                                                                    <td></td>
                                                                </tr>
                                                            </tfoot>
                                                        </Table>
                                                    </div>
                                                </CardBody>
                                            </Card>
                                        )}
                                    </CardBody>
                                </Card>
                            </Col>

                        {/* Boutons d'action */}
                        {activeStep !== 2 && activeStep !== 4 && <PaymentFinancialSummary paymentAmount={values.montantRegle} allocatedAmount={calculateTotalAmount()} currentPaymentAllocated={calculateCurrentPaymentAllocation(selectedDocuments)} advanceAmount={calculateAdvanceUsageAmount(selectedDocuments)} selectedCount={selectedDocuments.length} partyKind="fournisseur" />}
                        <Col id="supplier-payment-step-4" md={12} className={`payment-form-right-item payment-form-action-item ${activeStep === 4 ? '' : 'd-none'}`}>
                        <Card className="supplier-payment-form-actionbar payment-form-panel">
                            <CardBody>
                                <PaymentFinancialSummary paymentAmount={values.montantRegle} allocatedAmount={calculateTotalAmount()} currentPaymentAllocated={calculateCurrentPaymentAllocation(selectedDocuments)} advanceAmount={calculateAdvanceUsageAmount(selectedDocuments)} selectedCount={selectedDocuments.length} partyKind="fournisseur" />
                                <div className="payment-review-grid mb-3">
                                    <div><span>Fournisseur</span><strong>{selectedFournisseur?.label || '-'}</strong></div>
                                    <div><span>Entreprise</span><strong>{companies?.find(company => Number(company.id) === Number(values.companyId))?.name || defaultCompany?.name || '-'}</strong></div>
                                    <div><span>Date</span><strong>{values.dateReglement || '-'}</strong></div>
                                    <div><span>Mode</span><strong>{modeReglementOptions.find(option => option.value === values.modeReglement)?.label || values.modeReglement || '-'}</strong></div>
                                    <div><span>Montant payé</span><strong>{formatPrice(values.montantRegle || 0, false)} DH</strong></div>
                                    <div><span>Référence</span><strong>{values.referencePaiement || values.numeroCheque || '-'}</strong></div>
                                </div>
                                <PaymentHelp color="warning">Après confirmation, les soldes et statuts des documents sélectionnés seront recalculés.</PaymentHelp>
                                <div className="d-flex justify-content-between align-items-center">
                                    <div>
                                        {selectedDocuments.length > 0 && (
                                            <small className="text-muted">
                                                {selectedDocuments.length} {t('reglement.documentsSelectionnes')} -
                                                {t('reglement.total')}: <strong>{formatPrice(calculateTotalAmount())}</strong>
                                            </small>
                                        )}
                                    </div>
                                    {
                                        canEdit && <div className="d-flex gap-2">

                                            <Button
                                                type="submit"
                                                color="primary"
                                                className="payment-primary-action"
                                                disabled={
                                                    isSubmitting ||
                                                    (!selectedFournisseur) ||
                                                    ((!values.montantRegle || values.montantRegle <= 0) && calculateAdvanceUsageAmount(selectedDocuments) <= 0)
                                                }
                                            >
                                                {isSubmitting && <Spinner size="sm" className="me-2" />}
                                                <i className={`fas ${reglement ? 'fa-save' : 'fa-plus'} me-1`}></i>
                                                {isSubmitting ? 'Enregistrement du paiement et mise à jour des soldes…' : reglement ? 'Enregistrer les modifications du règlement' : paymentPurpose === 'ADVANCE' ? 'Enregistrer l’avance' : 'Confirmer le paiement fournisseur'}
                                            </Button>


                                        </div>}
                                </div>
                            </CardBody>
                        </Card>
                        </Col>
                        </aside>
                        </div>
                    </Form>
                )}
            </Formik>

            {/* Modal de sélection des documents */}
            <DocumentSelectionModal
                isOpen={showDocumentModal}
                toggle={() => setShowDocumentModal(false)}
                typeDocument={typeReglement}
                documents={availableDocuments}
                loading={loadingReglements}
                selectedDocuments={selectedDocuments}
                onDocumentsSelected={handleDocumentsSelected}
            />
            <Modal isOpen={showEcheanceModal} toggle={() => setShowEcheanceModal(false)} size="lg">
                <ModalHeader toggle={() => setShowEcheanceModal(false)} className="bg-warning bg-opacity-10 border-0">
                    <FaCalendarAlt className="me-2 text-warning" />
                    <span className="fw-bold text-warning">{t('client.echeancesDetails')}</span>
                </ModalHeader>
                <ModalBody>
                    {echeances && echeances.length > 0 ? (
                        <div>
                            <div className="mb-3 d-flex justify-content-between align-items-center">
                                <span className="fw-bold text-warning fs-5">
                                    <FaCalendarAlt className="me-2" /> {t('client.echeances')}: {echeances.length}
                                </span>
                                <span className="fw-bold text-muted">{t('echeance.total')}: <span className="text-warning">{formatPrice(getTotalMontant(echeances))}</span></span>
                            </div>
                            <Table bordered hover responsive className="table-sm">
                                <thead className="bg-warning bg-opacity-10">
                                    <tr>
                                        <th>{t('echeance.reference')}</th>
                                        <th>{t('echeance.dateReglement')}</th>
                                        <th>{t('echeance.numeroCheque')}</th>
                                        <th>{t('echeance.banque')}</th>
                                        <th>{t('echeance.montantCheque')}</th>
                                        <th>{t('echeance.dateEcheance')}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {echeances.map((echeance, idx) => (
                                        <tr key={idx}>
                                            <td>{echeance.reference}</td>
                                            <td>{echeance.dateReglement}</td>
                                            <td>{echeance.numeroCheque}</td>
                                            <td>{echeance.banque}</td>
                                            <td className="text-end fw-bold text-warning">{formatPrice(echeance.montantRegle)}</td>
                                            <td>{echeance.dateEcheance}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </div>
                    ) : (
                        <div className="text-muted text-center py-4">{t('client.noEcheances')}</div>
                    )}
                </ModalBody>
            </Modal>
            <Modal isOpen={showCautionModal} toggle={() => setShowCautionModal(false)} size="lg">
                <ModalHeader toggle={() => setShowCautionModal(false)} className="bg-info bg-opacity-10 border-0">
                    <FaShieldAlt className="me-2 text-info" />
                    <span className="fw-bold text-info">{t('client.cautionsDetails')}</span>
                </ModalHeader>
                <ModalBody>
                    {cautions && cautions.length > 0 ? (
                        <div>
                            <div className="mb-3 d-flex justify-content-between align-items-center">
                                <span className="fw-bold text-info fs-5">
                                    <FaShieldAlt className="me-2" /> {t('client.cautions')}: {cautions.length}
                                </span>
                                <span className="fw-bold text-muted">{t('caution.total')}: <span className="text-info">{formatPrice(getTotalMontant(cautions))}</span></span>
                            </div>
                            <Table bordered hover responsive className="table-sm">
                                <thead className="bg-info bg-opacity-10">
                                    <tr>
                                        <th>{t('caution.reference')}</th>
                                        <th>{t('caution.dateReglement')}</th>
                                        <th>{t('caution.numeroCheque')}</th>
                                        <th>{t('caution.banque')}</th>
                                        <th>{t('caution.montantCheque')}</th>
                                        <th>{t('caution.dateEcheance')}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {cautions.map((caution, idx) => (
                                        <tr key={idx}>
                                            <td>{caution.reference}</td>
                                            <td>{caution.dateReglement}</td>
                                            <td>{caution.numeroCheque}</td>
                                            <td>{caution.banque}</td>
                                            <td className="text-end fw-bold text-info">{formatPrice(caution.montantRegle)}</td>
                                            <td>{caution.dateEcheance}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </div>
                    ) : (
                        <div className="text-muted text-center py-4">{t('client.noCautions')}</div>
                    )}
                </ModalBody>
            </Modal>
        </Container>
    );
};

export default ReglementFournisseurForm;

